import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import React, { useState } from 'react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from 'components/ui/chart';
import { Bar, ResponsiveContainer } from 'recharts';
import { BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { StyleGuide } from 'styles/StyleGuide';
import { ChartDataLiter } from './hooks/useLevelSensorData';
import { formatRoundedLiterValue, formatRoundedYAxisTick } from './chartFormatters';

export default function WaterUsageGraph({
  waterLiterData,
  maxLiter,
}: {
  waterLiterData: ChartDataLiter[];
  maxLiter: number;
}) {
  const [showTotalUsage, setShowTotalUsage] = useState(false);

  const formatXAxis = (timestamp: string) => {
    const date = new Date(timestamp);
    return `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  };

  const waterUsageDisplay = !showTotalUsage
    ? waterLiterData.map((item) => ({ ...item, liter: item?.liter - waterLiterData[0].liter }))
    : waterLiterData;

  const maxLiterDisplay = !showTotalUsage
    ? waterLiterData[waterLiterData.length - 1]?.liter - waterLiterData[0]?.liter
    : maxLiter;

  return (
    <div className="flex flex-wrap rounded-lg border bg-card p-6">
      <div className="flex items-center space-x-2 w-full justify-end">
        <Switch id="water-liters" checked={showTotalUsage} onCheckedChange={setShowTotalUsage} />
        <Label htmlFor="water-liters">
          {showTotalUsage ? 'Total akkumuleret forbrug' : 'Relative forbrug for denne periode'}
        </Label>
      </div>
      <ChartContainer
        config={{
          waterLevel: {
            label: '(l)',
            color: StyleGuide.palette.processing, // Blue color for water
          },
        }}
        className="h-[300px] w-full"
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={waterUsageDisplay}
            margin={{ top: 10, right: 10, left: 10, bottom: 30 }}
            accessibilityLayer
            barSize={waterLiterData.length > 100 ? 2 : waterLiterData.length > 50 ? 5 : 10}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="timestamp"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tickFormatter={formatXAxis}
              interval={Math.floor(waterLiterData.length / 10)} // Show fewer ticks for readability
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              allowDecimals={false}
              tickFormatter={formatRoundedYAxisTick}
              domain={[0, maxLiterDisplay]}
              label={{ value: 'Liter (l)', angle: -90, position: 'insideLeft' }}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(_, data) => data[0]?.payload.formattedTime || ''}
                  formatter={(value, name) => {
                    if (name === 'liter') {
                      return [formatRoundedLiterValue(value)];
                    }
                    return [value, name];
                  }}
                />
              }
            />
            <Bar
              dataKey="liter"
              fill="var(--color-waterLevel)"
              radius={[4, 4, 0, 0]}
              name="liter"
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
}
